import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { Button, Col, Row } from 'react-bootstrap';
import { useAbility } from '@casl/react';
import { debounceMarkingCorrectionReportRequest } from '../../../requests/attendaceRecord';
import { ComponentDataTable, Icon, DefaultModal } from '../../../components';
import { AbilityContext } from '../../../config/abilityContext';
import { downloadFile } from '../../../services/utils';
import AttendanceDetailReportFilters from '../../AttendanceDetailReport/AttendanceDetailReportFilters';
import { sendAlert } from '../../../actions/utils';
import ReportActions from '../../SimpleReports/ReportActions';
import MarkingCorrectionHistoryChangesTable from './MarkingCorrectionHistoryChangesTable';

const ChangesExpander = ({ data }) => {
  const { changes } = data;
  return <MarkingCorrectionHistoryChangesTable changes={changes} />;
};

const columns = () => [
  {
    name: 'Nombre',
    selector: 'name',
    cell: item => <>{item.full_name}</>,
    sortable: false,
    grow: '2'
  },
  {
    name: 'Cédula de identidad (RUT)',
    selector: 'national_identification',
    sortable: false,
    grow: '1'
  },
  {
    name: 'Lugar de Prestación de Servicio',
    selector: 'branch_office',
    sortable: false,
    grow: '1'
  }
];

const MarkingCorrectionHistoryReportDataTable = ({
  rrhh,
  filters: propFilters,
  moreData: propMoreData,
  setMoreData: propSetMoreData
}) => {
  const [amount, setAmount] = useState(0);
  const [data, setData] = useState([]);
  const [isSubmitting, setSubmitting] = useState(false);
  const [moreData, setMoreData] = useState(false);
  const [onRequest, setOnRequest] = useState(false);
  const [searchFirstStart, setSearchFirstStart] = useState(false);
  const [filtersState, setFiltersState] = useState();
  const [showFilter, setShowFilter] = useState(true);
  const [dateRange, setDateRange] = useState([]);
  const [modalShow, setModalShow] = useState(false);
  const [tile, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [parameters, setParameters] = useState([]);

  const dispatch = useDispatch();
  const ability = useAbility(AbilityContext);

  const activeMoreData = typeof propMoreData !== 'undefined' ? propMoreData : moreData;

  const handleFailureRequest = error => {
    const message = error?.response?.data?.message || 'ERROR AL RECOLECTAR DATOS';
    dispatch(sendAlert({ kind: 'error', message }));
  };

  const handleRequest = (params, format = null, type = 'json') => {
    const responseType = type;
    const responseFormat = format;
    const customParams = {
      ...params
    };

    const finalParams = { ...customParams };
    finalParams.date_from = finalParams.date_from_workday;
    finalParams.date_to = finalParams.date_to_workday;

    delete finalParams.employee_id;
    delete finalParams.employee_ids;
    delete finalParams.filter_id;
    delete finalParams.filter_ids;
    delete finalParams.date_from_workday;
    delete finalParams.date_to_workday;
    delete finalParams.range_date_workday;

    setOnRequest(true);
    setSubmitting(true);
    debounceMarkingCorrectionReportRequest({
      dispatch,
      params: {
        display_start: 0,
        display_length: 15,
        ...finalParams,
        sort_direction: 'asc',
        sort_column: 'name',
        is_dt: false,
        paginate: format === null
      },
      format: responseFormat,
      responseType,
      successCallback: response => {
        const { data: responseData } = response;
        if (format === '.pdf' || format === '.xlsx') {
          downloadFile(response);
        } else {
          setData(responseData.data || []);
          setAmount(responseData.metadata?.amount || responseData.data?.length || 0);
        }
      },
      failureCallback: handleFailureRequest,
      callback: () => {
        setOnRequest(false);
        setSubmitting(false);
      }
    });
  };

  const handleMassAction = action => {
    switch (action) {
      case 'download_excel':
        setTitle('Exportar Historial de Corrección de Marcaciones');
        setBody('¿Estás seguro que quieres exportar este reporte en formato Excel?');
        setParameters([{ ...filtersState, type: '.xlsx' }, '.xlsx', 'text']);
        setModalShow(true);
        break;
      case 'download_pdf':
        setTitle('Exportar Historial de Corrección de Marcaciones');
        setBody('¿Estás seguro que quieres exportar este reporte en formato PDF?');
        setParameters([{ ...filtersState, type: '.pdf' }, '.pdf', 'text']);
        setModalShow(true);
        break;
      default:
        console.log('Action not supported for this report');
    }
  };

  const handleFilterAdvance = async params => {
    setFiltersState(params);
    setDateRange(params.range_date_workday);
    if (params.type === null) {
      setSearchFirstStart(true);
      if (typeof propSetMoreData === 'function') propSetMoreData(!propMoreData);
      else setMoreData(prev => !prev);
      setShowFilter(false);
    } else {
      handleRequest(params, params.type, params.type === '.pdf' ? 'blob' : 'json');
    }
  };

  const sortColumnCase = name => ({ sort_column: name });

  return (
    <>
      <div className={showFilter ? '' : 'd-none'}>
        <AttendanceDetailReportFilters
          filterRRHH={rrhh}
          formRequest={handleFilterAdvance}
          isSubmit={isSubmitting}
          onRequest={onRequest}
          onlyPreview
        />
      </div>

      <div className={showFilter ? 'd-none' : ''}>
        <Row>
          <Col md={6} className="mt-3">
            <Button variant="primary" onClick={() => setShowFilter(true)}>
              <Icon icon="chevron-back" width={15} />
              <span className="arial-dt">Volver al filtro</span>
            </Button>
          </Col>
        </Row>

        <ComponentDataTable
          onRequest={onRequest}
          columns={columns()}
          moreData={activeMoreData}
          data={data}
          responsive
          totalRows={amount}
          handleSortCase={sortColumnCase}
          withMassActions={ability.can('export', 'MarkingCorrectionHistoryReport')}
          massActions={
            <ReportActions disabled={onRequest} handleClick={handleMassAction} dates={dateRange} attrr downloadPdf />
          }
          resourceRequest={params => {
            handleRequest({ ...params, ...filtersState });
          }}
          expandableRows
          expandableRowExpanded={() => true}
          expandableRowsComponent={<ChangesExpander />}
          searchFirstStart={searchFirstStart}
          noDataComponentText="NO HAY DATOS QUE COINCIDAN CON LA SELECCIÓN"
        />
      </div>

      <DefaultModal
        title={tile}
        body={body}
        show={modalShow}
        handleClose={() => {
          setModalShow(false);
          setSubmitting(false);
        }}
        handleConfirm={() => {
          handleRequest(...parameters);
          setModalShow(false);
        }}
        titleBtnClose="Cancelar"
        titleBtnSave="Confirmar"
      />
    </>
  );
};

export default MarkingCorrectionHistoryReportDataTable;
